import { isNil } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { FormPreview } from '@/entities/contract'
import { TaxReceiptType } from '@/entities/user'
import { formatPhoneNumber, useScrollState } from '@/shared/lib'

import { ReceiptForm } from './receipt-form'
import { formatTaxInvoiceNumber } from '../lib/format'
import { TReceiptInformationFormValues } from '../model/form'

type Props = {
  defaultFormOpen: boolean
  hasDefaultReceiptInformation: boolean
  onFormOpen?: () => void
  scrollBlock?: ScrollLogicalPosition
}

export const ReceiptInformationUpdate = ({
  defaultFormOpen = false,
  hasDefaultReceiptInformation = false,
  onFormOpen,
  scrollBlock = 'end',
}: Props) => {
  const [isFormOpen, setIsFormOpen] = useState(defaultFormOpen)

  const { scrollIntoView } = useScrollState<HTMLDivElement>()

  const { control } = useFormContext<TReceiptInformationFormValues>()

  const [
    receiptRequestedValue,
    taxReceiptType,
    personalIssuanceNumber,
    businessIssuanceNumber,
    corpId,
  ] = useWatch({
    control,
    name: [
      'receiptRequested',
      'taxReceiptType',
      'personalIssuanceNumber',
      'businessIssuanceNumber',
      'corpId',
    ],
  })

  const getDisplayReceiptInformation = useMemo((): string | null => {
    if (isNil(receiptRequestedValue)) {
      return null
    }

    const receiptRequested = receiptRequestedValue === 'true'

    if (!receiptRequested) {
      return '신청 안 함'
    }

    if (taxReceiptType === TaxReceiptType.CASH_RECEIPT_PERSONAL) {
      return `신청 (현금영수증 ${formatPhoneNumber(personalIssuanceNumber as string)})`
    }

    if (taxReceiptType === TaxReceiptType.CASH_RECEIPT_BUSINESS) {
      return `신청 (현금영수증 ${formatPhoneNumber(businessIssuanceNumber as string)})`
    }

    if (taxReceiptType === TaxReceiptType.TAX_INVOICE) {
      return `신청 (전자세금계산서 ${formatTaxInvoiceNumber(corpId as string)})`
    }

    return ''
  }, [
    businessIssuanceNumber,
    corpId,
    personalIssuanceNumber,
    receiptRequestedValue,
    taxReceiptType,
  ])

  const handleEditButtonClick = () => {
    setIsFormOpen(true)
    onFormOpen?.()

    setTimeout(() => {
      scrollIntoView({ block: scrollBlock, behavior: 'smooth' })
    }, 0)
  }

  useEffect(() => {
    if (!hasDefaultReceiptInformation) {
      setIsFormOpen(defaultFormOpen)

      if (defaultFormOpen) {
        onFormOpen?.()
      }
    }
  }, [hasDefaultReceiptInformation, defaultFormOpen])

  if (!isFormOpen) {
    return (
      <FormPreview
        name="영수증 정보"
        value={getDisplayReceiptInformation}
        onEditButtonClick={handleEditButtonClick}
      />
    )
  }

  return <ReceiptForm scrollBlock={scrollBlock} />
}
